import React, { useEffect, useState } from "react";
import axios from "axios";
import { Briefcase, MapPin, Clock, ArrowUpRight, Eye } from "lucide-react";
import SectionLabel from "./brand/SectionLabel";
import JobModal from "./JobModal";
import ApplyModal from "./ApplyModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public careers section. Renders nothing if the careers toggle is off
 * or there are no published jobs.
 */
export default function Careers() {
  const [enabled, setEnabled] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [applyJob, setApplyJob] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, j] = await Promise.all([
          axios.get(`${API}/settings/careers`),
          axios.get(`${API}/jobs`),
        ]);
        if (cancelled) return;
        setEnabled(!!s.data?.enabled);
        const list = Array.isArray(j.data) ? j.data : [];
        setJobs(list);

        // Deep-link: ?job=<id> auto-opens the matching job modal
        try {
          const params = new URLSearchParams(window.location.search);
          const jobId = params.get("job");
          if (jobId) {
            const match = list.find((x) => x.id === jobId);
            if (match) {
              setActiveJob(match);
              // Smooth-scroll into view shortly after render
              setTimeout(() => {
                document.getElementById("careers")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 100);
            }
          }
        } catch (err) {
          // Deep-link parsing failed — non-fatal, just log.
          console.warn("Job deep-link parse failed:", err?.message || err);
        }
      } catch {
        if (!cancelled) {
          setEnabled(false);
          setJobs([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!enabled || jobs.length === 0) return null;

  const applyHref = (job) => {
    if (job.apply_method === "url" && job.apply_value) return job.apply_value;
    const email = job.apply_value || "rod@epicom.ca";
    const subject = encodeURIComponent(`Application — ${job.title}`);
    return `mailto:${email}?subject=${subject}`;
  };

  return (
    <section
      id="careers"
      data-testid="careers-section"
      className="relative py-24 md:py-32 bg-bg-secondary border-t border-steel-dark"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-12 gap-8 mb-12">
          <div className="md:col-span-6">
            <SectionLabel className="mb-5">Help Wanted</SectionLabel>
            <h2 className="heading text-4xl md:text-5xl lg:text-6xl text-white uppercase leading-[0.95]">
              Join the <br />
              <span className="text-signal">Crew.</span>
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex items-end">
            <p className="text-steel-light text-base md:text-lg leading-relaxed">
              We're growing. If you're handy, customer-focused, and like working with wireless and networking gear, we'd love to hear from you. Training provided for the right person.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-[1px] bg-steel-dark border border-steel-dark">
          {jobs.map((job) => (
            <article
              key={job.id}
              data-testid={`careers-job-${job.id}`}
              className="group relative bg-bg-primary p-8 md:p-10 transition-colors hover:bg-[#161a1f] cursor-pointer"
              onClick={() => setActiveJob(job)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setActiveJob(job);
                }
              }}
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <Briefcase className="text-signal" size={28} strokeWidth={1.5} />
                <span className="font-mono text-[10px] tracking-[0.25em] text-signal uppercase border border-signal/40 px-3 py-1.5">
                  Now Hiring
                </span>
              </div>
              <h3 className="heading text-2xl md:text-3xl text-white uppercase leading-tight mb-3 group-hover:text-signal transition-colors">
                {job.title}
              </h3>
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-steel-light font-mono uppercase tracking-[0.15em] mb-5">
                {job.location && (
                  <span className="inline-flex items-center gap-2">
                    <MapPin size={12} className="text-signal" />
                    {job.location}
                  </span>
                )}
                {job.employment_type && (
                  <span className="inline-flex items-center gap-2">
                    <Clock size={12} className="text-signal" />
                    {job.employment_type}
                  </span>
                )}
                {job.compensation && (
                  <span className="text-signal">{job.compensation}</span>
                )}
              </div>

              {job.summary && (
                <p className="text-steel-light text-sm md:text-base leading-relaxed mb-6">
                  {job.summary}
                </p>
              )}

              <div
                className="flex flex-wrap items-center gap-3"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => setActiveJob(job)}
                  data-testid={`careers-job-${job.id}-toggle`}
                  className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-steel-light hover:text-signal border border-steel-dark hover:border-signal px-5 py-3 transition-colors"
                >
                  <Eye size={14} /> Read more
                </button>
                {job.apply_method === "form" ? (
                  <button
                    type="button"
                    onClick={() => setApplyJob(job)}
                    data-testid={`careers-job-${job.id}-apply`}
                    className="group/apply inline-flex items-center gap-2 bg-signal hover:bg-signal-hover text-white font-bold uppercase tracking-wider text-sm px-6 py-3 transition-colors"
                  >
                    Apply Now
                    <ArrowUpRight
                      size={16}
                      className="group-hover/apply:translate-x-1 group-hover/apply:-translate-y-1 transition-transform"
                    />
                  </button>
                ) : (
                  <a
                    href={applyHref(job)}
                    target={job.apply_method === "url" ? "_blank" : undefined}
                    rel={job.apply_method === "url" ? "noopener noreferrer" : undefined}
                    data-testid={`careers-job-${job.id}-apply`}
                    className="group/apply inline-flex items-center gap-2 bg-signal hover:bg-signal-hover text-white font-bold uppercase tracking-wider text-sm px-6 py-3 transition-colors"
                  >
                    Apply Now
                    <ArrowUpRight
                      size={16}
                      className="group-hover/apply:translate-x-1 group-hover/apply:-translate-y-1 transition-transform"
                    />
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>

      <JobModal job={activeJob} onClose={() => setActiveJob(null)} />
      <ApplyModal job={applyJob} onClose={() => setApplyJob(null)} />
    </section>
  );
}
